/**
 * GFG-273 (Campaigns tab, GFG Admin) — the tab's testable rules as pure functions: no React, no
 * I/O, no clock. This repo has no DOM test environment, so anything worth a test lives here and
 * the components only call. Functions that depend on the current instant take `now` as a
 * parameter, the same discipline as utils/sendQueueCampaigns-helper.ts. UI strings come from
 * constants/adminCampaigns.ts and CSV assembly delegates to lib/csv.ts.
 */
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import {
  type AdminCampaignColumnId,
  CAMPAIGNS_CSV_COLUMNS,
  FILTER_IDS,
  PAGE_SIZE,
} from "@/constants/adminCampaigns";
import { CAMPAIGN_STATUSES, type CampaignStatus } from "@/constants/campaings";
import {
  ADMIN_CAMPAIGN_SEND_METHODS,
  ADMIN_CAMPAIGN_STATUSES,
  ADMIN_CAMPAIGN_TYPES,
  type AdminCampaignContactStatus,
  type AdminCampaignRow,
  type AdminCampaignSendMethod,
  type AdminCampaignSortKey,
  type AdminCampaignStatus,
  type AdminCampaignsListParams,
  type AdminCampaignType,
} from "@/interfaces/adminCampaigns";
import { buildCsv, type CsvColumn } from "@/lib/csv";

// Lives in the leaf number-format so `constants/` can format a rate without importing this file,
// which imports `constants/` back; re-exported here because it reads as one of the tab's helpers.
export { formatRate } from "@/utils/number-format";

/** An already-redeemed contact keeps its redemption record, so it can never be removed. */
const UNREMOVABLE_STATUS: AdminCampaignContactStatus = "Redeemed";

/** Milliseconds since the epoch, or null for an absent/unparseable date so it sorts last. */
function toInstant(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * One comparable value per sort key. Dates become instants here so the comparison never falls
 * back to lexicographic order on the ISO string.
 */
const SORT_VALUES: Record<AdminCampaignSortKey, (row: AdminCampaignRow) => string | number | null> =
  {
    name: (row) => row.name,
    client: (row) => row.organization,
    account_lead: (row) => row.accountLead,
    collection_value: (row) => row.collectionValue,
    status: (row) => row.status,
    recipients: (row) => row.recipients,
    scheduled_at: (row) => toInstant(row.scheduledAt),
    expiry: (row) => toInstant(row.expiry),
    campaign_type: (row) => row.campaignType,
    send_method: (row) => row.sendMethod,
    open_rate: (row) => row.openRate,
    redemption_rate: (row) => row.redemptionRate,
    bounces: (row) => row.bounces,
    scheduled_reminders: (row) => row.scheduledReminders,
    thank_you_messages: (row) => row.thankYouMessages,
  };

/** Text compares alphabetically and case-insensitively; everything else compares numerically. */
function compareValues(a: string | number, b: string | number): number {
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "accent" });
  }
  return Number(a) - Number(b);
}

/**
 * Sorts a copy of `rows`. Null values collect at the end in BOTH directions — a campaign with no
 * open rate is missing data, not the smallest value — and ties keep their input order, so
 * `sortDir` never reverses rows the user cannot tell apart.
 */
export function sortCampaigns(
  rows: AdminCampaignRow[],
  sortBy: AdminCampaignSortKey,
  sortDir: "asc" | "desc",
): AdminCampaignRow[] {
  const sortValue = SORT_VALUES[sortBy];
  const direction = sortDir === "asc" ? 1 : -1;

  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const aValue = sortValue(a.row);
      const bValue = sortValue(b.row);
      if (aValue === null || bValue === null) {
        if (aValue === bValue) return a.index - b.index;
        return aValue === null ? 1 : -1;
      }
      const compared = compareValues(aValue, bValue);
      return compared === 0 ? a.index - b.index : compared * direction;
    })
    .map((entry) => entry.row);
}

/**
 * The campaigns export: the two fixed data columns, then the visible columns in the order the
 * caller has them on screen (SPEC A2.4). Quoting, escaping and formula-injection neutralisation
 * are `buildCsv`'s job, shared with every other export in the app.
 */
export function buildCampaignsCsv(
  rows: AdminCampaignRow[],
  visibleColumnIds: readonly AdminCampaignColumnId[],
): string {
  const columns: CsvColumn<AdminCampaignRow>[] = [
    CAMPAIGNS_CSV_COLUMNS.campaignName,
    CAMPAIGNS_CSV_COLUMNS.client,
    ...visibleColumnIds.map((id) => CAMPAIGNS_CSV_COLUMNS[id]),
  ];
  return buildCsv(columns, rows);
}

/** The admin table's status in the client lifecycle vocabulary the shared modals are driven by. */
export function toClientStatus(status: AdminCampaignStatus): CampaignStatus {
  switch (status) {
    case CAMPAIGN_STATUSES.ADMIN_DRAFT:
      return CAMPAIGN_STATUSES.DRAFT;
    case CAMPAIGN_STATUSES.ADMIN_SCHEDULED:
      return CAMPAIGN_STATUSES.SCHEDULED;
    case CAMPAIGN_STATUSES.ADMIN_PAUSED:
      return CAMPAIGN_STATUSES.PAUSED;
    case CAMPAIGN_STATUSES.ADMIN_CLOSED:
      return CAMPAIGN_STATUSES.CLOSED;
    case CAMPAIGN_STATUSES.ADMIN_CANCELED:
      return CAMPAIGN_STATUSES.CANCELED;
    case CAMPAIGN_STATUSES.ADMIN_ARCHIVED:
      return CAMPAIGN_STATUSES.ARCHIVED;
    default:
      return CAMPAIGN_STATUSES.ACTIVE;
  }
}

/** Whether the remove icon is live: locked campaigns and redeemed gifts are both untouchable. */
export function canRemoveContact(
  contact: { status: string },
  lock: "paused" | "ended" | null,
): boolean {
  return lock === null && contact.status !== UNREMOVABLE_STATUS;
}

const isStatus = (v: string): v is AdminCampaignStatus =>
  ADMIN_CAMPAIGN_STATUSES.some((s) => s === v);
const isType = (v: string): v is AdminCampaignType => ADMIN_CAMPAIGN_TYPES.some((t) => t === v);
const isSendMethod = (v: string): v is AdminCampaignSendMethod =>
  ADMIN_CAMPAIGN_SEND_METHODS.some((m) => m === v);

/**
 * Chips, search text, sort and page as querystring params. Values outside a vocabulary are dropped
 * here rather than sent: the API answers an unknown value with a 400, which would be the panel's
 * bug and not a state a user can reach.
 */
export function toListParams(
  search: string,
  filters: AppliedFilter[],
  sortBy: AdminCampaignSortKey,
  sortDir: "asc" | "desc",
  page: number,
): AdminCampaignsListParams {
  const valuesFor = (id: string) => filters.find((f) => f.fieldId === id)?.values ?? [];
  const [scheduledFrom = "", scheduledTo = ""] = valuesFor(FILTER_IDS.scheduled);
  const [expiryFrom = "", expiryTo = ""] = valuesFor(FILTER_IDS.expiry);
  return {
    search: search || undefined,
    organization_id: valuesFor(FILTER_IDS.organization),
    status: valuesFor(FILTER_IDS.status).filter(isStatus),
    campaign_type: valuesFor(FILTER_IDS.campaignType).filter(isType),
    send_method: valuesFor(FILTER_IDS.sendMethod).filter(isSendMethod),
    scheduled_from: scheduledFrom || undefined,
    scheduled_to: scheduledTo || undefined,
    expiry_from: expiryFrom || undefined,
    expiry_to: expiryTo || undefined,
    sortBy,
    sortDir,
    page,
    limit: PAGE_SIZE,
    includeFilterOptions: true,
  };
}
